import { Slot } from '@radix-ui/react-slot'
import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/styles'

import { Spinner } from '../elementary/Spinner'

const buttonVariants = cva(
	'inline-flex items-center justify-center rounded-full disabled:pointer-events-none disabled:opacity-50 transition-colors duration-200 ease-in-out cursor-pointer',
	{
		variants: {
			variant: {
				primary:
					'bg-neutral-dark-300 hover:bg-brand-black text-neutral-light-100 leading-1 text-center sm:text-left',
				secondary:
					'border border-brand-black active:border-brand-black text-neutral-dark-300  hover:text-brand-black leading-1 text-center sm:text-left',
				google:
					'text-neutral-light-100 bg-blue-700 border border-blue-700 lok-text-body-bold-large leading-1 text-center sm:text-left',
				pillowLarge:
					'border border-neutral-light-alpha-500 hover:border-brand-black bg-transparent text-neutral-dark-300 hover:text-brand-black hover:bg-neutral-dark-alpha-500 active:bg-brand-black active:text-neutral-light-100 active:border-brand-black lok-text-body-bold-medium',
				pillowSmall:
					'border border-neutral-light-alpha-500 hover:border-brand-black bg-transparent text-neutral-dark-300 hover:text-brand-black hover:bg-neutral-dark-alpha-500 text-sm/2 lg:text-base/3 active:bg-brand-black active:text-neutral-light-100 active:border-brand-black',
				switch:
					'border border-neutral-light-alpha-500 hover:border-brand-black bg-transparent text-neutral-dark-300 hover:text-brand-black hover:bg-neutral-dark-alpha-500 active:bg-brand-black active:text-neutral-light-100 active:border-brand-black lok-text-body-bold-small',
				primaryLink:
					'text-brand-black rounded-none  font-bold text-lg/4 lg:text-3xl/11 focus-visible:text-brand-salmon hover:text-brand-salmon active:text-brand-salmon',
				secondaryLink:
					'text-neutral-dark-300  rounded-none font-bold lg:text-2xl/11 text-base/4 focus-visible:text-brand-salmon hover:text-brand-salmon active:text-brand-salmon',
				tertiaryLink:
					'text-neutral-dark-300 rounded-none font-normal underline text-2xl/9 lg:text-2xl/7 focus-visible:text-brand-salmon hover:text-brand-salmon active:text-brand-salmon',
				quaternaryLink:
					'text-neutral-dark-300 rounded-none font-normal underline text-lg/5 lg:text-lg/7 focus-visible:text-brand-salmon hover:text-brand-salmon active:text-brand-salmon',
				none: '',
				ghost: 'hover:text-brand-salmon duration-200 transition-colors',
				inverse:
					'border border-brand-black bg-white text-brand-black focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-black hover:bg-neutral-light-400 active:bg-neutral-light-500  lok-text-body-bold-large',
			},
			size: {
				large: 'h-12 lg:h-17 px-8 lg:px-10 lok-text-body-bold-large',
				medium: 'h-10 lg:h-12 px-6 lok-text-body-bold-medium',
				small: 'h-9 px-5 lok-text-body-bold-medium',
				pillow: 'px-3 py-0.75',
				switch: 'px-6 py-1.5',
				none: 'p-0',
			},
		},
		defaultVariants: {
			variant: 'primary',
			size: 'medium',
		},
	},
)

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean
	isLoading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, isLoading, ...props }, ref) => {
		const Comp = asChild ? Slot : 'button'

		return (
			<Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
				{isLoading ? <Spinner className="h-4 w-4 border-2 " /> : props.children}
			</Comp>
		)
	},
)
Button.displayName = 'Button'

export { Button, buttonVariants }
